import { useCallback, useEffect, useRef, useState } from "react";

const PULSE_MS = 550;

export type PulseIconState = {
  type: "play" | "pause" | "mute" | "unmute";
  key: number;
} | null;

export type SkipPulseState = {
  direction: "backward" | "forward";
  seconds: number;
  key: number;
} | null;

/**
 * Transient badges flashed over the picture: play / pause / mute in the centre,
 * a skip badge on the side the playhead jumped to. Shared by the fullscreen
 * player and the multi-camera wall so both feel the same.
 *
 * `key` is what restarts the CSS animation when the same badge fires twice.
 */
export function usePlayerPulse() {
  const [pulseIcon, setPulseIcon] = useState<PulseIconState>(null);
  const [skipPulse, setSkipPulse] = useState<SkipPulseState>(null);

  const pulseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const skipTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
    };
  }, []);

  const triggerPulse = useCallback((type: NonNullable<PulseIconState>["type"]) => {
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    setPulseIcon({ type, key: Date.now() });
    pulseTimerRef.current = setTimeout(() => {
      setPulseIcon(null);
      pulseTimerRef.current = null;
    }, PULSE_MS);
  }, []);

  const triggerSkipPulse = useCallback(
    (direction: NonNullable<SkipPulseState>["direction"], seconds: number) => {
      if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
      setSkipPulse({ direction, seconds, key: Date.now() });
      skipTimerRef.current = setTimeout(() => {
        setSkipPulse(null);
        skipTimerRef.current = null;
      }, PULSE_MS);
    },
    []
  );

  return { pulseIcon, skipPulse, triggerPulse, triggerSkipPulse };
}
